import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const NAV = [
  { key: "about", href: "#about" },
  { key: "approach", href: "#approach" },
  { key: "model", href: "#model" },
  { key: "capabilities", href: "#capabilities" },
  { key: "contact", href: "#contact" },
];

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-1.5 font-sans text-[11px] tracking-[0.1em] ${className}`} data-testid="lang-toggle">
      {["en", "fr"].map((l, i) => (
        <span key={l} className="flex items-center gap-1.5">
          {i === 1 && <span className="text-paper/30">/</span>}
          <button
            type="button"
            onClick={() => setLang(l)}
            data-testid={`lang-${l}`}
            aria-pressed={lang === l}
            className={`uppercase transition-colors duration-200 ${lang === l ? "text-teal" : "text-paper/55 hover:text-paper"}`}
          >
            {l}
          </button>
        </span>
      ))}
    </div>
  );
};

const BrandMark = () => (
  <a href="#top" data-testid="brand-logo" className="group inline-flex items-center gap-3 text-paper">
    <img
      src="/images/osrelis-mark.png"
      alt="OSRELIS"
      className="h-9 w-9 object-contain transition-transform duration-500 group-hover:rotate-[8deg] md:h-10 md:w-10"
    />
    <span className="font-sans text-[15px] font-semibold tracking-[0.32em]">OSRELIS</span>
  </a>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { c } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-[90] flex items-center justify-between px-6 transition-[height,background-color,backdrop-filter] duration-500 md:px-[4vw] ${
          scrolled
            ? "h-[68px] border-b border-paper/10 bg-deep/85 backdrop-blur-xl"
            : "h-[88px] border-b border-transparent bg-transparent"
        }`}
      >
        <BrandMark />
        <nav className="hidden items-center gap-7 lg:flex">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.key}`}
              className="whitespace-nowrap font-sans text-xs tracking-[0.08em] text-paper/70 transition-colors duration-300 hover:text-teal"
            >
              {c.nav[n.key]}
            </a>
          ))}
        </nav>
        <div className="hidden items-center gap-6 lg:flex">
          <a
            href="mailto:CONTACT@osrelis.com"
            data-testid="header-email"
            className="whitespace-nowrap border-b border-paper/40 pb-1 font-sans text-xs tracking-[0.06em] text-paper transition-colors duration-300 hover:border-teal hover:text-teal"
          >
            CONTACT@osrelis.com
          </a>
          <LangToggle />
        </div>
        <div className="flex items-center gap-4 lg:hidden">
          <LangToggle />
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="menu-open"
            aria-label="Open menu"
            className="text-paper"
          >
            <Menu size={22} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[95] flex flex-col bg-deep px-7 pb-10 pt-8 lg:hidden"
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <BrandMark />
              <button type="button" onClick={() => setOpen(false)} aria-label="Close menu" data-testid="menu-close" className="text-paper">
                <X size={24} />
              </button>
            </div>
            <div className="mt-16 flex flex-col gap-6">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.key}`}
                  className="font-serif text-4xl text-paper"
                >
                  {c.nav[n.key]}
                </a>
              ))}
            </div>
            <a href="mailto:CONTACT@osrelis.com" className="mt-auto font-sans text-sm text-mist">
              CONTACT@osrelis.com
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
